import {
  LearningTrackTopic,
  LearningTrackTopicComponent,
  SessionContext,
  SessionContextMode,
  TopicContextItem,
  TopicContextItemType,
} from "../types/ai-session.entity";
import { getContextItemId, normalizeContextText } from "./sessionContextItems";

type AISessionContextSource = {
  context?: unknown;
  topics?: unknown;
};

type LegacySessionContextMode = SessionContextMode | "CUSTOM_SCENARIO";

type TopicScenarioResource = {
  situation?: unknown;
};

type TopicScenarioLookup = Map<string, TopicScenarioResource | undefined>;

const SESSION_CONTEXT_MODES: LegacySessionContextMode[] = [
  "TOPIC",
  "LEARNING_TRACK",
  "WORD_BANK",
  "CUSTOM_SCENARIO",
];

const TOPIC_CONTEXT_ITEM_TYPES: TopicContextItemType[] = ["TOPIC", "CUSTOM_SCENARIO"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isSessionContextMode = (value: unknown): value is LegacySessionContextMode =>
  SESSION_CONTEXT_MODES.includes(value as LegacySessionContextMode);

const isTopicContextItemType = (value: unknown): value is TopicContextItemType =>
  TOPIC_CONTEXT_ITEM_TYPES.includes(value as TopicContextItemType);

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const inferContextModeFromItems = (items: unknown[]): SessionContextMode => {
  if (
    items.some(
      (item) =>
        isRecord(item) &&
        (Array.isArray(item.components) ||
          typeof item.topicTitle === "string" ||
          typeof item.title === "string")
    )
  ) {
    return "LEARNING_TRACK";
  }

  if (
    items.some(
      (item) =>
        isRecord(item) &&
        ("wordId" in item ||
          "word" in item ||
          "grammarLabel" in item ||
          "translation" in item)
    )
  ) {
    return "WORD_BANK";
  }

  return "TOPIC";
};

const normalizeLearningTrackComponents = (value: unknown): LearningTrackTopicComponent[] =>
  asArray(value)
    .map((component) => {
      if (!isRecord(component)) {
        return null;
      }

      const content = normalizeContextText(component.content);
      if (!content) {
        return null;
      }

      return {
        content,
        type: component.type === "QUESTION" ? "QUESTION" : "SENTENCE",
      };
    })
    .filter((component): component is LearningTrackTopicComponent => Boolean(component));

const normalizeLearningTrackItems = (items: unknown[]): LearningTrackTopic[] =>
  items.map((item, index) => {
    if (!isRecord(item)) {
      return {
        topicTitle: normalizeContextText(item) ?? `Learning Track Topic ${index + 1}`,
        components: [],
      };
    }

    return {
      topicTitle:
        normalizeContextText(item.topicTitle) ??
        normalizeContextText(item.title) ??
        normalizeContextText(item.topicSlug) ??
        `Learning Track Topic ${index + 1}`,
      components: normalizeLearningTrackComponents(item.components),
    };
  });

const normalizeTopicContextItem = (
  value: unknown,
  defaultType: TopicContextItemType = "TOPIC"
): TopicContextItem | null => {
  if (!isRecord(value)) {
    const id = normalizeContextText(value);
    return id ? { type: defaultType, id } : null;
  }

  const explicitType = isTopicContextItemType(value.type) ? value.type : undefined;
  const inferredType =
    explicitType ??
    ("scenarioId" in value || "situation" in value || "defaultLearnerRole" in value
      ? "CUSTOM_SCENARIO"
      : "TOPIC");
  const id =
    inferredType === "CUSTOM_SCENARIO"
      ? normalizeContextText(value.id) ??
        normalizeContextText(value.scenarioId) ??
        getContextItemId(value)
      : normalizeContextText(value.id) ??
        normalizeContextText(value.topicSlug) ??
        normalizeContextText(value.topicId) ??
        normalizeContextText(value.slug) ??
        getContextItemId(value);

  return id ? { type: inferredType, id } : null;
};

const normalizeTopicContextItems = (
  items: unknown[],
  defaultType: TopicContextItemType = "TOPIC"
): TopicContextItem[] =>
  items
    .map((item) => normalizeTopicContextItem(item, defaultType))
    .filter((item): item is TopicContextItem => Boolean(item));

const getUniqueValues = (values: string[]): string[] => {
  const byNormalized = new Map<string, string>();

  values.forEach((value) => {
    const normalizedValue = normalizeContextText(value);
    if (!normalizedValue) return;

    const normalizedKey = normalizedValue.toLowerCase();
    if (!byNormalized.has(normalizedKey)) {
      byNormalized.set(normalizedKey, normalizedValue);
    }
  });

  return Array.from(byNormalized.values());
};

export const getTopicContextItems = (items: TopicContextItem[]): TopicContextItem[] => items;

export const getTopicContextTopicIds = (items: TopicContextItem[]): string[] =>
  getUniqueValues(items.filter((item) => item.type === "TOPIC").map((item) => item.id));

export const getTopicContextCustomScenarioIds = (items: TopicContextItem[]): string[] =>
  getUniqueValues(
    items.filter((item) => item.type === "CUSTOM_SCENARIO").map((item) => item.id)
  );

export const getTopicContextScenarioDisplayLabel = (
  scenarioById: TopicScenarioLookup | undefined,
  scenarioId: string
): string =>
  normalizeContextText(scenarioById?.get(scenarioId)?.situation) ?? scenarioId;

export const getTopicContextDisplayLabel = (
  item: TopicContextItem,
  scenarioById?: TopicScenarioLookup
): string =>
  item.type === "TOPIC"
    ? item.id
    : getTopicContextScenarioDisplayLabel(scenarioById, item.id);

export const getTopicContextDisplayLabels = (
  items: TopicContextItem[],
  scenarioById?: TopicScenarioLookup
): string[] =>
  getUniqueValues(items.map((item) => getTopicContextDisplayLabel(item, scenarioById)));

export const getAISessionContext = (
  session: AISessionContextSource
): SessionContext => {
  const rawContext = isRecord(session.context) ? session.context : undefined;
  const rawItems = asArray(rawContext?.items);
  const legacyTopics = asArray(session.topics);
  const rawContextSessionMode = rawContext?.sessionMode;
  const rawSessionMode = isSessionContextMode(rawContextSessionMode)
    ? rawContextSessionMode
    : undefined;
  const sessionMode: SessionContextMode =
    rawSessionMode === "CUSTOM_SCENARIO"
      ? "TOPIC"
      : rawSessionMode ??
        (rawItems.length > 0
          ? inferContextModeFromItems(rawItems)
          : legacyTopics.length > 0
            ? "TOPIC"
            : "TOPIC");

  switch (sessionMode) {
    case "LEARNING_TRACK":
      return {
        sessionMode,
        items: normalizeLearningTrackItems(rawItems),
      };
    case "WORD_BANK":
      return {
        sessionMode,
        items: rawItems
          .map((item) => getContextItemId(item))
          .filter((item): item is string => Boolean(item)),
      };
    case "TOPIC":
    default: {
      const topicItemsSource =
        rawItems.length > 0 ? rawItems : rawSessionMode === "TOPIC" ? legacyTopics : legacyTopics;
      const defaultTopicItemType: TopicContextItemType =
        rawSessionMode === "CUSTOM_SCENARIO" ? "CUSTOM_SCENARIO" : "TOPIC";

      return {
        sessionMode: "TOPIC",
        items: normalizeTopicContextItems(topicItemsSource, defaultTopicItemType),
      };
    }
  }
};

export const normalizeAISession = <T extends { context?: unknown; topics?: unknown }>(
  session: T
): T & { context: SessionContext } => ({
  ...session,
  context: getAISessionContext(session),
});
